/**
 * The follow-up types, transcribed by hand from the frozen `.forge/plans/F3-a/F3-a-Wave-1-contract.md`
 * `## Field Definitions` (F3-a WI-1.2 plan Decision 1). Each interface names exactly the members the
 * contract declares; `activitiesApi.ts` narrows every body into them from `unknown`. Untrusted text —
 * `title`, `notes`, `record.name`, every `displayName` — is rendered as a React text child only
 * (contract § Untrusted text).
 */

/** `ActivityType` — an element of row 1's bare array, and an item's `type`. Exactly three members. */
export interface ActivityType {
    readonly id: number;
    /** Lower-case and stable (`call`, `meeting`, `task`, `deadline`, `email`, `lunch` built in). */
    readonly key: string;
    /** Shown to the user (*Task*). */
    readonly label: string;
}

/** `ActivityUser` — an item's `assignee`, `done.by` and a detail's `createdBy`. Exactly three members (F3-a Wave 1 contract D-7). */
export interface ActivityUser {
    readonly id: number;
    /** `null` only if none is recorded; the screen shows `—`, never the id. */
    readonly displayName: string | null;
    /** `false` for a deactivated user; the screen adds *(deactivated)* after the name. */
    readonly active: boolean;
}

export const ACTIVITY_RECORD_TYPES = ['account', 'lead'] as const;

/** The kind of record a follow-up is linked to. */
export type ActivityRecordType = (typeof ACTIVITY_RECORD_TYPES)[number];

/** `ActivityRecord` — the one linked account or lead. */
export interface ActivityRecord {
    readonly type: ActivityRecordType;
    readonly id: number;
    /** An account's name, or a lead's company name else its contact name (F3-a Wave 1 contract D-8); never `null`. */
    readonly name: string;
}

/** `ActivityDone` — `null` on an open follow-up. */
export interface ActivityDone {
    /** ISO-8601 UTC instant. The chip reads its Colombo date. */
    readonly at: string;
    readonly by: ActivityUser;
}

export const ACTIVITY_BUCKETS = ['overdue', 'today', 'upcoming', 'completed'] as const;

/** The server's bucket (contract `### What each filter selects`) — never recomputed by the client (F3-a Wave 1 contract D-12). */
export type ActivityBucket = (typeof ACTIVITY_BUCKETS)[number];

/** The three open buckets — what `bucket=` takes and what row 3 counts. */
export const OPEN_BUCKETS = ['overdue', 'today', 'upcoming'] as const;

export type OpenBucket = (typeof OPEN_BUCKETS)[number];

/** `ActivityItem` — row 2's item. Exactly eleven members; `notes` is not one (F3-a Wave 1 contract D-1). */
export interface ActivityItem {
    readonly id: number;
    readonly type: ActivityType;
    readonly title: string;
    /** `yyyy-MM-dd`, a Colombo business date. */
    readonly dueDate: string;
    /** `HH:mm`, or `null` when none was set. */
    readonly dueTime: string | null;
    readonly bucket: ActivityBucket;
    readonly assignee: ActivityUser;
    readonly record: ActivityRecord;
    readonly done: ActivityDone | null;
    readonly needsReassigning: boolean;
    /** The optimistic-lock version, sent by *Mark done* (F3-a Wave 2 contract row 8). */
    readonly version: number;
}

/** `ActivityDetail` — row 4, and row 5's `201` body: the item's eleven members plus four (fifteen). */
export interface ActivityDetail extends ActivityItem {
    readonly notes: string | null;
    readonly createdAt: string;
    readonly createdBy: ActivityUser | null;
    readonly modifiedAt: string;
}

/** `ActivityCounts` — row 3. The open follow-ups per bucket under a scope. */
export type ActivityCounts = Readonly<Record<OpenBucket, number>>;

/** `AssigneeCandidate` — row 6's item. Exactly three members (F3-a Wave 1 contract D-11: `displayName`, not `name`). */
export interface AssigneeCandidate {
    readonly id: number;
    readonly displayName: string | null;
    /** The role's display name, shown in brackets; never a decision input (AD #26). */
    readonly roleName: string;
}

/** The one record a follow-up is created on, or whose assignee candidates are read. */
export interface ActivityRecordRef {
    readonly type: ActivityRecordType;
    readonly id: number;
}

/**
 * `ActivityCreateRequest` — row 5's body. Exactly one of `accountId` / `leadId`; `dueTime`, `notes`
 * and `assigneeId` are left out when not set (F3-a WI-1.2 plan Decision 8). `title` is sent as typed; the
 * server trims it.
 */
export type ActivityCreateRequest = ({ readonly accountId: number } | { readonly leadId: number }) & {
    readonly typeId: number;
    readonly title: string;
    readonly dueDate: string;
    readonly dueTime?: string;
    readonly notes?: string;
    readonly assigneeId?: number;
};

/**
 * `ActivityPatchRequest` — row 7's body (F3-a Wave 2 contract `### ActivityPatchRequest`, D-2, D-3).
 * `version` plus any of six members; an **absent** member leaves the stored value as it is, and `null`
 * clears only `dueTime` and `notes`. `accountId` and `leadId` are not members: the record never changes.
 * `followUpForm.ts`'s `toPatchRequest` builds it with only the members that changed (F3-a WI-2.2 plan
 * Decision 3).
 */
export interface ActivityPatchRequest {
    readonly version: number;
    readonly typeId?: number;
    /** Sent as typed; the server trims it. */
    readonly title?: string;
    readonly dueDate?: string;
    /** `HH:mm`, or `null` to clear the time. */
    readonly dueTime?: string | null;
    /** As typed, or `null` to clear the notes. */
    readonly notes?: string | null;
    readonly assigneeId?: number;
}

/**
 * `ActivityStatusChangeRequest` — the body of rows 8 and 9 (F3-a Wave 2 contract
 * `### ActivityStatusChangeRequest`): exactly `{ version }`, the version last read.
 */
export interface ActivityStatusChangeRequest {
    readonly version: number;
}

/** `view=` on row 2. */
export type ActivityView = 'open' | 'completed';

/** `scope=` on rows 2 and 3: *Mine* or *All I can see*. */
export type ActivityScope = 'mine' | 'reach';

/** What the Activities page asks row 2 for (F3-a WI-1.2 plan Decision 8). */
export interface ActivitiesListParams {
    readonly view: ActivityView;
    readonly scope: ActivityScope;
    /** A chosen bucket pill; read only with `view: 'open'`. `null` is *All*. */
    readonly bucket: OpenBucket | null;
    /** 1-based. */
    readonly page: number;
    readonly size: number;
}
